import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../Controls/BreadCrumb";
import CustomButton from "../Controls/CustomButton";
import { ModalBusyProvider, ModalBusyOverlay, useModalBusy } from "../Controls/modalBusy";
import EmployerEditModal from "../Modals/EmployerEditModal";
import API from "../../API";
import { toast } from "react-toastify";
import "../../style/scss/JobIntake.scss";

const SAMPLE_PASTE =
  "Senior .NET Developer needed for our client in Austin. Required: C#, Azure. Benefits: medical, 401k. Must be US citizen. Bachelor's preferred. Contact Jane at jane@staffing.example";

function parseJsonSafe(text, fallback) {
  if (!text) return fallback;
  try {
    return JSON.parse(text);
  } catch {
    return fallback;
  }
}

function hostFromUrl(url) {
  try {
    return new URL((url || "").trim()).host.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function storedCookieFor(url) {
  const host = hostFromUrl(url);
  if (!host) return null;
  return window.localStorage.getItem(`jobIntakeCookie:${host}`) || null;
}

function randomMs(minMs, maxMs) {
  const lo = Math.min(minMs, maxMs);
  const hi = Math.max(minMs, maxMs);
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function normalizeDetail(raw) {
  if (!raw) return null;
  return {
    id: raw.id ?? raw.ID,
    status: raw.status ?? raw.Status,
    sourceUrl: raw.sourceUrl ?? raw.SourceUrl,
    sourceText: raw.sourceText ?? raw.SourceText ?? "",
    aiDraftJson: raw.aiDraftJson ?? raw.AiDraftJson,
    humanFinalJson: raw.humanFinalJson ?? raw.HumanFinalJson,
    annotationsJson: raw.annotationsJson ?? raw.AnnotationsJson,
    modelName: raw.modelName ?? raw.ModelName,
    promptVersion: raw.promptVersion ?? raw.PromptVersion,
    titleHint: raw.titleHint ?? raw.TitleHint,
    jobID: raw.jobID ?? raw.JobID,
    discoveredFromIntakeID: raw.discoveredFromIntakeID ?? raw.DiscoveredFromIntakeID,
  };
}

function normalizeFetchResponse(raw) {
  if (!raw) return null;
  const primary = normalizeDetail(raw.primary ?? raw.Primary);
  return {
    primary,
    relatedJobs: normalizeRelatedList(raw.relatedJobs ?? raw.RelatedJobs),
    nextPageUrl: raw.nextPageUrl ?? raw.NextPageUrl,
    delayMinMs: raw.delayMinMs ?? raw.DelayMinMs,
    delayMaxMs: raw.delayMaxMs ?? raw.DelayMaxMs,
    currentPage: raw.currentPage ?? raw.CurrentPage,
    estimatedTotalPages: raw.estimatedTotalPages ?? raw.EstimatedTotalPages,
    estimatedTotalJobs: raw.estimatedTotalJobs ?? raw.EstimatedTotalJobs,
    jobsFoundOnPage: raw.jobsFoundOnPage ?? raw.JobsFoundOnPage,
    pageKind: raw.pageKind ?? raw.PageKind,
  };
}

function normalizeRelatedList(raw) {
  const list = Array.isArray(raw)
    ? raw
    : raw?.relatedJobs ?? raw?.RelatedJobs ?? [];
  if (!Array.isArray(list)) return [];
  return list
    .map((j) => ({
      url: (j.url ?? j.Url ?? "").trim(),
      titleHint: j.titleHint ?? j.TitleHint ?? "",
      proximitySnippet: j.proximitySnippet ?? j.ProximitySnippet ?? "",
      queuedIntakeId: j.queuedIntakeId ?? j.QueuedIntakeId ?? j.queuedIntakeID ?? null,
    }))
    .filter((j) => j.url);
}

function relatedFromExtraction(human) {
  if (!human) return [];
  return normalizeRelatedList(human.relatedJobs ?? human.RelatedJobs);
}

const ORG_ROLES = [
  { key: "employer", label: "Employer (hiring company)" },
  { key: "client", label: "Client" },
  { key: "intermediary", label: "Intermediary / recruiting firm" },
];

function normalizeOrgRole(role) {
  const r = (role || "").trim().toLowerCase();
  if (["employer", "hiring company", "hiringcompany", "company", "direct"].includes(r))
    return "employer";
  if (["client", "end client", "endclient"].includes(r)) return "client";
  if (
    [
      "intermediary",
      "recruiter",
      "recruiting firm",
      "staffing",
      "staffing firm",
      "agency",
      "presenter",
      "job board",
      "jobboard",
    ].includes(r)
  )
    return "intermediary";
  return "unknown";
}

function pickField(obj, ...keys) {
  for (const k of keys) {
    if (obj?.[k] !== undefined && obj?.[k] !== null) return obj[k];
  }
  return undefined;
}

function normalizeOrg(o) {
  return {
    name: pickField(o, "name", "Name") ?? "",
    role: normalizeOrgRole(pickField(o, "role", "Role")),
    employerID: pickField(o, "employerID", "EmployerID") ?? null,
    url: pickField(o, "url", "Url") ?? "",
    description: pickField(o, "description", "Description") ?? "",
    phone: pickField(o, "phone", "Phone") ?? "",
    shortName: pickField(o, "shortName", "ShortName") ?? "",
    wikiUrl: pickField(o, "wikiUrl", "WikiUrl") ?? "",
    domainKey: pickField(o, "domainKey", "DomainKey") ?? "",
    researched: !!pickField(o, "researched", "Researched"),
    evidenceQuote: pickField(o, "evidenceQuote", "EvidenceQuote") ?? "",
  };
}

function emptyOrg(role) {
  return {
    name: "",
    role,
    employerID: null,
    url: "",
    description: "",
    phone: "",
    shortName: "",
    wikiUrl: "",
    domainKey: "",
    researched: false,
    evidenceQuote: "",
  };
}

// Always produce exactly three slots (employer, client, intermediary) so the
// reviewer can see every possible party and create any that aren't in the system.
function normalizeOrgSlots(orgs) {
  const list = Array.isArray(orgs) ? orgs.map(normalizeOrg) : [];
  const byRole = {};
  const leftovers = [];
  for (const o of list) {
    if (["employer", "client", "intermediary"].includes(o.role) && !byRole[o.role])
      byRole[o.role] = o;
    else leftovers.push(o);
  }
  for (const o of leftovers) {
    const hasContent = o.employerID > 0 || o.name || o.url || o.description;
    if (!hasContent) continue;
    const target = ORG_ROLES.map((r) => r.key).find((k) => !byRole[k]);
    if (!target) break;
    byRole[target] = { ...o, role: target };
  }
  return ORG_ROLES.map((r) => byRole[r.key] || emptyOrg(r.key));
}

function JobIntakeDetailInner() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { withBusy } = useModalBusy();
  const isNew = routeId === "new";

  const [detail, setDetail] = useState(null);
  const [sourceText, setSourceText] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [extraction, setExtraction] = useState(null);
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [employerRoleID, setEmployerRoleID] = useState("");
  const [employerEditId, setEmployerEditId] = useState(null);
  const [extractionTab, setExtractionTab] = useState("jobInfo");
  const [fetchCookie, setFetchCookie] = useState("");

  // Session cookie is remembered per site (browser localStorage only — never stored server-side).
  const sourceHost = useMemo(() => hostFromUrl(sourceUrl), [sourceUrl]);

  useEffect(() => {
    if (!sourceHost) return;
    setFetchCookie(window.localStorage.getItem(`jobIntakeCookie:${sourceHost}`) ?? "");
  }, [sourceHost]);

  const updateFetchCookie = (value) => {
    setFetchCookie(value);
    if (!sourceHost) return;
    const key = `jobIntakeCookie:${sourceHost}`;
    if (value.trim()) window.localStorage.setItem(key, value.trim());
    else window.localStorage.removeItem(key);
  };
  const [skillCategories, setSkillCategories] = useState([]);
  const [browse, setBrowse] = useState(null);
  const [countdownSec, setCountdownSec] = useState(null);
  const browseTimer = useRef(null);
  const countdownTimer = useRef(null);

  const intakeId = detail?.id ?? (isNew ? null : Number(routeId));

  const lockedSource = useMemo(() => {
    const st = detail?.status;
    return st && st !== "Draft" && st !== "FetchFailed";
  }, [detail?.status]);

  const posted = detail?.status === "Posted";

  const persistBrowseState = useCallback((state) => {
    setBrowse(state);
    if (state) window.sessionStorage.setItem("JobIntakeBrowseState", JSON.stringify(state));
    else window.sessionStorage.removeItem("JobIntakeBrowseState");
  }, []);

  const applyDetail = useCallback((d, relatedOverride) => {
    const normalized = normalizeDetail(d);
    setDetail(normalized);
    setSourceText(normalized?.sourceText ?? "");
    setSourceUrl(normalized?.sourceUrl ?? "");
    const human = parseJsonSafe(normalized?.humanFinalJson, null);
    setExtraction(human);
    setAnnotations(parseJsonSafe(normalized?.annotationsJson, []));
    if (relatedOverride !== undefined) {
      setRelatedJobs(normalizeRelatedList(relatedOverride));
    } else {
      setRelatedJobs(relatedFromExtraction(human));
    }
  }, []);

  const loadDetail = useCallback(async () => {
    if (isNew) {
      applyDetail(null, []);
      setSourceText("");
      setSourceUrl("");
      setExtraction(null);
      setAnnotations([]);
      setRelatedJobs([]);
      return;
    }
    const resp = await API.getAsync(`JobPostingIntake/${routeId}`);
    if (resp?.ok) applyDetail(resp.jsonData);
    else toast.error(resp?.message || "Failed to load intake");
  }, [applyDetail, isNew, routeId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    API.getAsync("Skill/Categories").then((resp) => {
      if (resp?.ok && Array.isArray(resp.jsonData)) setSkillCategories(resp.jsonData);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (browseTimer.current) clearTimeout(browseTimer.current);
      if (countdownTimer.current) clearInterval(countdownTimer.current);
    };
  }, []);

  const updateExtraction = (patch) => {
    setExtraction((prev) => ({ ...(prev || {}), ...patch }));
  };

  const runExtract = async () => {
    if (!sourceText || sourceText.trim().length < 40) {
      toast.error("Source text must be at least 40 characters before extracting");
      return;
    }
    await withBusy(async () => {
      const resp = await API.postAsync("JobPostingIntake/Extract", {
        sourceText,
        sourceUrl: sourceUrl || null,
        id: intakeId,
      });
      if (resp?.ok) {
        const normalized = normalizeDetail(resp.jsonData);
        const fromServer = relatedFromExtraction(
          parseJsonSafe(normalized?.humanFinalJson, null)
        );
        applyDetail(normalized, fromServer.length > 0 ? fromServer : relatedJobs);
        if (normalized?.id && String(normalized.id) !== String(routeId)) {
          navigate(`/job-intake/${normalized.id}`, { replace: true });
        }
        toast.success(intakeId ? "Extraction updated" : "Extracted");
      } else toast.error(resp?.message || "Extract failed");
    });
  };

  const runFetch = async (urlOverride, extract = true) => {
    const url = (urlOverride || sourceUrl || "").trim();
    if (!url) {
      toast.error("Source URL is required");
      return;
    }
    await withBusy(async () => {
      const resp = await API.postAsync("JobPostingIntake/FetchUrl", {
        url,
        id: intakeId,
        extract,
        cookie: storedCookieFor(url) ?? (fetchCookie.trim() || null),
      });
      if (!resp?.ok) {
        toast.error(resp?.message || "Fetch failed");
        return;
      }
      const data = normalizeFetchResponse(resp.jsonData);
      const fromExtraction = relatedFromExtraction(
        parseJsonSafe(data.primary?.humanFinalJson, null)
      );
      const related =
        data.relatedJobs?.length > 0 ? data.relatedJobs : fromExtraction;
      applyDetail(data.primary, related);
      navigate(`/job-intake/${data.primary.id}`, { replace: true });

      if (data.nextPageUrl) {
        persistBrowseState({
          nextPageUrl: data.nextPageUrl,
          delayMinMs: data.delayMinMs,
          delayMaxMs: data.delayMaxMs,
          currentPage: data.currentPage,
          estimatedTotalPages: data.estimatedTotalPages,
          estimatedTotalJobs: data.estimatedTotalJobs,
          jobsImported: (browse?.jobsImported ?? 0) + (data.jobsFoundOnPage ?? 0),
          parentIntakeId: data.primary.id,
        });
      } else {
        persistBrowseState(null);
      }
      const n = related.length;
      toast.success(
        n > 0 ? `Fetched URL — ${n} related job${n === 1 ? "" : "s"} found` : "Fetched URL"
      );
    });
  };

  const clearBrowseTimers = () => {
    if (browseTimer.current) clearTimeout(browseTimer.current);
    if (countdownTimer.current) clearInterval(countdownTimer.current);
    setCountdownSec(null);
  };

  const stopBrowsing = () => {
    clearBrowseTimers();
    persistBrowseState(null);
  };

  const startKeepBrowsing = () => {
    if (!browse?.nextPageUrl) return;
    const nextUrl = browse.nextPageUrl;
    const waitMs = randomMs(browse.delayMinMs ?? 2000, browse.delayMaxMs ?? 8000);
    clearBrowseTimers();
    const sec = Math.ceil(waitMs / 1000);
    setCountdownSec(sec);
    countdownTimer.current = setInterval(() => {
      setCountdownSec((s) => (s == null ? null : s <= 1 ? null : s - 1));
    }, 1000);
    browseTimer.current = setTimeout(() => {
      runFetch(nextUrl, true);
    }, waitMs);
  };

  const saveReview = async () => {
    if (!intakeId) {
      toast.error("Extract or fetch first");
      return;
    }
    await withBusy(async () => {
      const humanFinal = {
        ...(extraction || {}),
        relatedJobs,
      };
      const resp = await API.putAsync(`JobPostingIntake/${intakeId}/Review`, {
        humanFinal,
        annotations,
      });
      if (resp?.ok) {
        applyDetail(resp.jsonData, relatedJobs);
        toast.success("Review saved");
      } else toast.error(resp?.message || "Save failed");
    });
  };

  const createJob = async () => {
    if (!intakeId) return;
    await withBusy(async () => {
      const resp = await API.postAsync(`JobPostingIntake/${intakeId}/CreateJob`, {
        employerRoleID: employerRoleID ? Number(employerRoleID) : null,
      });
      if (resp?.ok) {
        const payload = resp.jsonData || {};
        applyDetail(payload.intake ?? payload.Intake);
        const jobId = payload.jobID ?? payload.JobID;
        toast.success(`Created job #${jobId}`);
      } else toast.error(resp?.message || "Create job failed");
    });
  };

  const rejectSkill = (index) => {
    setAnnotations((prev) => [
      ...prev.filter((a) => a.path !== `skills[${index}]`),
      { path: `skills[${index}]`, kind: "rejected", reason: "Rejected in review UI" },
    ]);
  };

  const addSkillToCatalog = async (index, skill) => {
    if (!intakeId) return;
    const categoryID = skillCategories[0]?.id ?? skillCategories[0]?.ID ?? 1;
    await withBusy(async () => {
      const resp = await API.postAsync(`JobPostingIntake/${intakeId}/Skills/Create`, {
        skillIndex: index,
        name: skill.name,
        categoryID,
        shortName: skill.name,
      });
      if (resp?.ok) {
        applyDetail(resp.jsonData);
        toast.success("Skill added to catalog");
      } else toast.error(resp?.message || "Skill create failed");
    });
  };

  const queueRelated = async (related) => {
    if (!intakeId) return;
    await withBusy(async () => {
      const resp = await API.postAsync(`JobPostingIntake/${intakeId}/QueueRelated`, {
        url: related.url ?? related.Url,
        titleHint: related.titleHint ?? related.TitleHint,
        proximitySnippet: related.proximitySnippet ?? related.ProximitySnippet,
      });
      if (!resp?.ok) {
        toast.error(resp?.message || "Queue failed");
        return;
      }
      const payload = resp.jsonData || {};
      const queuedId = payload.queuedIntakeID ?? payload.QueuedIntakeID;
      const already = !!(payload.alreadyQueued ?? payload.AlreadyQueued);
      const urlKey = (related.url ?? related.Url ?? "").trim().toLowerCase();
      setRelatedJobs((prev) =>
        prev.map((j) =>
          j.url.toLowerCase() === urlKey ? { ...j, queuedIntakeId: queuedId } : j
        )
      );
      toast.success(
        already
          ? `Already in queue (#${queuedId})`
          : `Added to import queue (#${queuedId})`
      );
    });
  };

  const queueAllRelated = async () => {
    const pending = relatedJobs.filter((j) => !j.queuedIntakeId);
    for (const job of pending) await queueRelated(job);
  };

  const skills = extraction?.skills ?? extraction?.Skills ?? [];
  const visibleSkillCount = skills.filter(
    (_, i) => !annotations.some((a) => a.path === `skills[${i}]` && a.kind === "rejected")
  ).length;
  const orgSlots = useMemo(
    () => normalizeOrgSlots(extraction?.organizations ?? extraction?.Organizations),
    [extraction]
  );

  const updateOrg = (role, patch) => {
    const next = orgSlots.map((s) => (s.role === role ? { ...s, ...patch } : s));
    updateExtraction({ organizations: next });
  };

  const createEmployerForOrg = async (slot) => {
    if (!intakeId) {
      toast.error("Extract or fetch first");
      return;
    }
    if (!slot.name?.trim()) {
      toast.error("Enter a name before creating the employer");
      return;
    }
    await withBusy(async () => {
      // Persist current edits first so nothing the reviewer changed is lost.
      const humanFinal = { ...(extraction || {}), organizations: orgSlots, relatedJobs };
      await API.putAsync(`JobPostingIntake/${intakeId}/Review`, { humanFinal, annotations });

      const resp = await API.postAsync(
        `JobPostingIntake/${intakeId}/Organizations/CreateEmployer`,
        {
          role: slot.role,
          name: slot.name,
          url: slot.url || null,
          description: slot.description || null,
          phone: slot.phone || null,
          shortName: slot.shortName || null,
          wikiUrl: slot.wikiUrl || null,
        }
      );
      if (resp?.ok) {
        const payload = resp.jsonData || {};
        applyDetail(payload.intake ?? payload.Intake, relatedJobs);
        const empId = payload.employerID ?? payload.EmployerID;
        toast.success(`Created employer #${empId}`);
      } else toast.error(resp?.message || "Create employer failed");
    });
  };

  return (
    <div className="job-intake-detail">
      <ModalBusyOverlay />
      <BreadCrumb props={["Admin", "Import job", isNew ? "New" : `#${routeId}`]} />
      <Link to="/job-intake">← Back to list</Link>

      <div className="job-intake-toolbar">
        <input
          type="url"
          placeholder="Source URL"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          disabled={posted}
        />
        <CustomButton variant="contained" disabled={posted} onClick={() => runFetch()}>
          Fetch from URL
        </CustomButton>
        <CustomButton variant="contained" disabled={posted} onClick={runExtract}>
          Extract
        </CustomButton>
        {browse?.nextPageUrl ? (
          <>
            <CustomButton variant="outlined" onClick={startKeepBrowsing}>
              Keep browsing
            </CustomButton>
            <CustomButton variant="text" onClick={stopBrowsing}>
              Stop
            </CustomButton>
          </>
        ) : null}
      </div>

      <details className="job-intake-advanced">
        <summary>
          Session cookie{sourceHost ? ` for ${sourceHost}` : ""}
          {fetchCookie.trim() ? " · set" : ""}
        </summary>
        <p className="job-intake-advanced-hint">
          Some job boards (ZipRecruiter, LinkedIn) block server fetches. Log in to the site in
          your browser, copy the <code>Cookie</code> header from DevTools (Network tab → any
          request → Request Headers), and paste it here. It is stored only in this browser,
          per site, and sent along with Fetch from URL.
        </p>
        <textarea
          rows={2}
          placeholder="name=value; name2=value2"
          value={fetchCookie}
          disabled={posted}
          onChange={(e) => updateFetchCookie(e.target.value)}
        />
      </details>

      {browse?.nextPageUrl ? (
        <div className="job-intake-browse-banner">
          Scraping page {browse.currentPage ?? "?"} of ~{browse.estimatedTotalPages ?? "?"}.
          Imported {browse.jobsImported ?? 0} jobs (~{browse.estimatedTotalJobs ?? "?"} total).
          {countdownSec != null ? ` Next page in ~${countdownSec}s…` : " Ready to continue."}
        </div>
      ) : null}

      <div className="job-intake-split">
        <section className="job-intake-pane">
          <h2>Source</h2>
          {lockedSource ? (
            <div className="job-intake-source-readonly">{sourceText}</div>
          ) : (
            <textarea
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              placeholder={SAMPLE_PASTE}
              rows={16}
            />
          )}
        </section>

        <section className="job-intake-pane">
          <h2>Extraction</h2>
          {!extraction ? <p>Run Extract or Fetch from URL to populate fields.</p> : null}
          {extraction ? (
            <>
              <div className="job-intake-tabs" role="tablist" aria-label="Extraction sections">
                {[
                  { id: "jobInfo", label: "Job Info" },
                  { id: "organizations", label: "Organizations" },
                  { id: "compensation", label: "Compensation" },
                  {
                    id: "skills",
                    label: `Skills${visibleSkillCount > 0 ? ` (${visibleSkillCount})` : ""}`,
                  },
                  {
                    id: "related",
                    label: `Related Jobs${relatedJobs.length > 0 ? ` (${relatedJobs.length})` : ""}`,
                  },
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    id={`job-intake-tab-${t.id}`}
                    aria-selected={extractionTab === t.id}
                    aria-controls={`job-intake-panel-${t.id}`}
                    className={extractionTab === t.id ? "is-active" : undefined}
                    onClick={() => setExtractionTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="job-intake-tab-panels">
                <div
                  id="job-intake-panel-jobInfo"
                  role="tabpanel"
                  aria-labelledby="job-intake-tab-jobInfo"
                  hidden={extractionTab !== "jobInfo"}
                  className={`job-intake-tab-panel${extractionTab === "jobInfo" ? " is-active" : ""}`}
                >
                  <div className="job-intake-field">
                    <label>Job title</label>
                    <p className="job-intake-related-hint">
                      Role name only — not the full posting headline (mode, location, pay, agency).
                    </p>
                    <input
                      value={extraction.title ?? extraction.Title ?? ""}
                      disabled={posted}
                      onChange={(e) => updateExtraction({ title: e.target.value })}
                    />
                    {(extraction.postingTitle ?? extraction.PostingTitle) &&
                    (extraction.postingTitle ?? extraction.PostingTitle) !==
                      (extraction.title ?? extraction.Title) ? (
                      <span className="job-intake-related-snippet">
                        Posting headline: {extraction.postingTitle ?? extraction.PostingTitle}
                      </span>
                    ) : null}
                  </div>
                  <div className="job-intake-field job-intake-work-mode" role="group" aria-label="Work arrangement">
                    <label>Work arrangement (critical for matching)</label>
                    <p className="job-intake-related-hint">
                      Remote: location is not a match gate. Onsite/hybrid: location is required for matching.
                    </p>
                    <div className="job-intake-work-mode-buttons">
                      {["onsite", "hybrid", "remote", "unknown"].map((mode) => {
                        const current =
                          (extraction.workArrangement?.mode ??
                            extraction.WorkArrangement?.Mode ??
                            "unknown"
                          ).toLowerCase();
                        return (
                          <button
                            key={mode}
                            type="button"
                            className={current === mode ? "is-active" : undefined}
                            disabled={posted}
                            onClick={() =>
                              updateExtraction({
                                workArrangement: {
                                  ...(extraction.workArrangement || extraction.WorkArrangement || {}),
                                  mode,
                                },
                              })
                            }
                          >
                            {mode}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="job-intake-field">
                    <label>
                      {(extraction.workArrangement?.mode ?? extraction.WorkArrangement?.Mode ?? "")
                        .toLowerCase() === "remote"
                        ? "Location (optional for remote)"
                        : "Location (critical when onsite/hybrid)"}
                    </label>
                    <textarea
                      rows={2}
                      disabled={posted}
                      value={(extraction.locations ?? extraction.Locations ?? [])
                        .map((l) => l.rawText || l.RawText || [l.city || l.City, l.state || l.State].filter(Boolean).join(", "))
                        .filter(Boolean)
                        .join("\n")}
                      onChange={(e) => {
                        const lines = e.target.value.split("\n").map((s) => s.trim()).filter(Boolean);
                        updateExtraction({
                          locations: lines.map((rawText) => ({ rawText })),
                        });
                      }}
                    />
                  </div>
                  <div className="job-intake-field">
                    <label>Employment type</label>
                    <select
                      disabled={posted}
                      value={extraction.employmentTypeID ?? extraction.EmploymentTypeID ?? ""}
                      onChange={(e) =>
                        updateExtraction({
                          employmentTypeID: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                    >
                      <option value="">Unknown</option>
                      <option value={10}>Full-time salary (W2)</option>
                      <option value={12}>Full-time hourly (W2)</option>
                      <option value={15}>Part-time (W2)</option>
                      <option value={20}>Corp-to-corp contract (C2C)</option>
                      <option value={30}>W2 contract</option>
                      <option value={40}>Contract-to-perm</option>
                      <option value={50}>Fixed bid</option>
                      <option value={60}>Volunteer</option>
                    </select>
                  </div>
                  <div className="job-intake-field">
                    <label>Apply URL</label>
                    <p className="job-intake-related-hint">
                      Link candidates use to apply (ATS, mailto, or this posting when Apply is on-page).
                    </p>
                    <input
                      type="url"
                      disabled={posted}
                      value={extraction.applyUrl ?? extraction.ApplyUrl ?? ""}
                      onChange={(e) => updateExtraction({ applyUrl: e.target.value })}
                      placeholder="https://… or mailto:…"
                    />
                    {(extraction.applyMethod ?? extraction.ApplyMethod) ? (
                      <span className="job-intake-related-snippet">
                        Method: {extraction.applyMethod ?? extraction.ApplyMethod}
                        {(extraction.applyLabel ?? extraction.ApplyLabel)
                          ? ` · Label: ${extraction.applyLabel ?? extraction.ApplyLabel}`
                          : ""}
                      </span>
                    ) : null}
                  </div>
                  <div className="job-intake-field">
                    <label>Source posting URL</label>
                    <input
                      type="url"
                      disabled={posted}
                      value={extraction.sourceUrl ?? extraction.SourceUrl ?? sourceUrl ?? ""}
                      onChange={(e) => updateExtraction({ sourceUrl: e.target.value })}
                    />
                  </div>
                  <div className="job-intake-field">
                    <label>Work authorization</label>
                    <textarea
                      readOnly
                      rows={2}
                      value={
                        extraction.workAuthorization?.summary ||
                        extraction.workAuthorization?.rawText ||
                        ""
                      }
                    />
                  </div>
                  <div className="job-intake-field">
                    <label>Education</label>
                    <textarea
                      readOnly
                      rows={2}
                      value={(extraction.educationRequirements ?? []).join("\n")}
                    />
                  </div>
                  <div className="job-intake-field">
                    <label>Certifications</label>
                    <textarea
                      readOnly
                      rows={2}
                      value={(extraction.certificationRequirements ?? [])
                        .map((c) => c.name)
                        .join("\n")}
                    />
                  </div>
                </div>

                <div
                  id="job-intake-panel-organizations"
                  role="tabpanel"
                  aria-labelledby="job-intake-tab-organizations"
                  hidden={extractionTab !== "organizations"}
                  className={`job-intake-tab-panel${extractionTab === "organizations" ? " is-active" : ""}`}
                >
                  <div className="job-intake-field job-intake-orgs">
                    <p className="job-intake-orgs-hint">
                      Every posting can involve up to three parties. Info found in the posting (and
                      researched from the source domain) is pre-filled below. If a party isn&apos;t in
                      the system yet, create it in one click.
                    </p>
                    {orgSlots.map((org) => {
                      const roleLabel =
                        ORG_ROLES.find((r) => r.key === org.role)?.label ?? org.role;
                      const matched = org.employerID > 0;
                      return (
                        <div
                          key={org.role}
                          className={`job-intake-org-card${matched ? " matched" : ""}`}
                        >
                          <div className="job-intake-org-head">
                            <strong>{roleLabel}</strong>
                            {matched ? (
                              <button
                                type="button"
                                className="job-intake-org-badge matched job-intake-org-open"
                                onClick={() => setEmployerEditId(org.employerID)}
                              >
                                In system · employer #{org.employerID}
                              </button>
                            ) : (
                              <span className="job-intake-org-badge missing">Not in system</span>
                            )}
                            {org.researched ? (
                              <span className="job-intake-org-badge researched">researched</span>
                            ) : null}
                          </div>
                          <div className="job-intake-org-grid">
                            <label>
                              Name
                              {matched ? (
                                <button
                                  type="button"
                                  className="job-intake-org-name-link"
                                  title="Open employer"
                                  onClick={() => setEmployerEditId(org.employerID)}
                                >
                                  {org.name || `Employer #${org.employerID}`}
                                </button>
                              ) : (
                                <input
                                  type="text"
                                  disabled={posted}
                                  value={org.name}
                                  onChange={(e) => updateOrg(org.role, { name: e.target.value })}
                                />
                              )}
                            </label>
                            <label>
                              Website
                              <input
                                type="url"
                                disabled={posted}
                                value={org.url}
                                onChange={(e) => updateOrg(org.role, { url: e.target.value })}
                              />
                            </label>
                            <label>
                              Phone
                              <input
                                type="text"
                                disabled={posted}
                                value={org.phone}
                                onChange={(e) => updateOrg(org.role, { phone: e.target.value })}
                              />
                            </label>
                            <label className="job-intake-org-desc">
                              Description
                              <textarea
                                rows={2}
                                disabled={posted}
                                value={org.description}
                                onChange={(e) =>
                                  updateOrg(org.role, { description: e.target.value })
                                }
                              />
                            </label>
                          </div>
                          <div className="job-intake-org-actions">
                            <label className="job-intake-org-id">
                              Employer ID
                              <input
                                type="number"
                                style={{ width: "5rem" }}
                                disabled={posted}
                                value={org.employerID ?? ""}
                                onChange={(e) =>
                                  updateOrg(org.role, {
                                    employerID: Number(e.target.value) || null,
                                  })
                                }
                              />
                            </label>
                            {!posted && !matched ? (
                              <CustomButton
                                size="small"
                                variant="outlined"
                                onClick={() => createEmployerForOrg(org)}
                              >
                                Create employer
                              </CustomButton>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="job-intake-field">
                    <label>Employer role ID (when employer org set)</label>
                    <input
                      type="number"
                      value={employerRoleID}
                      disabled={posted}
                      onChange={(e) => setEmployerRoleID(e.target.value)}
                    />
                  </div>
                </div>

                <div
                  id="job-intake-panel-compensation"
                  role="tabpanel"
                  aria-labelledby="job-intake-tab-compensation"
                  hidden={extractionTab !== "compensation"}
                  className={`job-intake-tab-panel${extractionTab === "compensation" ? " is-active" : ""}`}
                >
                  <div className="job-intake-field job-intake-compensation">
                    <label>Compensation</label>
                    <div className="job-intake-comp-grid">
                      <label>
                        Min
                        <input
                          type="number"
                          disabled={posted}
                          value={extraction.compensation?.min ?? extraction.compensation?.Min ?? ""}
                          onChange={(e) =>
                            updateExtraction({
                              compensation: {
                                ...(extraction.compensation || {}),
                                min: e.target.value === "" ? null : Number(e.target.value),
                              },
                            })
                          }
                        />
                      </label>
                      <label>
                        Max
                        <input
                          type="number"
                          disabled={posted}
                          value={extraction.compensation?.max ?? extraction.compensation?.Max ?? ""}
                          onChange={(e) =>
                            updateExtraction({
                              compensation: {
                                ...(extraction.compensation || {}),
                                max: e.target.value === "" ? null : Number(e.target.value),
                              },
                            })
                          }
                        />
                      </label>
                      <label>
                        Interval
                        <select
                          disabled={posted}
                          value={
                            (
                              extraction.compensation?.interval ??
                              extraction.compensation?.Interval ??
                              "annual"
                            ).toLowerCase()
                          }
                          onChange={(e) =>
                            updateExtraction({
                              compensation: {
                                ...(extraction.compensation || {}),
                                interval: e.target.value,
                              },
                            })
                          }
                        >
                          <option value="annual">Annual</option>
                          <option value="monthly">Monthly</option>
                          <option value="weekly">Weekly</option>
                          <option value="hourly">Hourly</option>
                          <option value="fixed">Fixed / project</option>
                        </select>
                      </label>
                    </div>
                    {(extraction.compensation?.rawText ?? extraction.compensation?.RawText) ? (
                      <span className="job-intake-related-snippet">
                        From posting:{" "}
                        {extraction.compensation?.rawText ?? extraction.compensation?.RawText}
                      </span>
                    ) : null}
                  </div>
                  <div className="job-intake-field">
                    <label>Benefits</label>
                    <textarea
                      readOnly
                      rows={2}
                      value={
                        extraction.benefits?.rawText ||
                        (extraction.benefits?.items ?? []).join(", ")
                      }
                    />
                  </div>
                </div>

                <div
                  id="job-intake-panel-skills"
                  role="tabpanel"
                  aria-labelledby="job-intake-tab-skills"
                  hidden={extractionTab !== "skills"}
                  className={`job-intake-tab-panel${extractionTab === "skills" ? " is-active" : ""}`}
                >
                  <div className="job-intake-field job-intake-skills">
                    <ul>
                      {skills.map((skill, i) => {
                        const rejected = annotations.some(
                          (a) => a.path === `skills[${i}]` && a.kind === "rejected"
                        );
                        if (rejected) return null;
                        const unmatched = !(skill.skillID > 0);
                        return (
                          <li key={i} className={unmatched ? "unmatched" : ""}>
                            <span>
                              {skill.name} ({skill.requirement})
                              {skill.skillID ? ` #${skill.skillID}` : " — unmatched"}
                              {(skill.evidenceQuote ?? skill.EvidenceQuote) ? (
                                <span className="job-intake-related-snippet">
                                  {" "}
                                  · {skill.evidenceQuote ?? skill.EvidenceQuote}
                                </span>
                              ) : null}
                            </span>
                            {!posted && unmatched ? (
                              <CustomButton
                                size="small"
                                variant="outlined"
                                onClick={() => addSkillToCatalog(i, skill)}
                              >
                                Add to catalog
                              </CustomButton>
                            ) : null}
                            {!posted ? (
                              <CustomButton size="small" variant="text" onClick={() => rejectSkill(i)}>
                                Reject
                              </CustomButton>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                    {visibleSkillCount === 0 ? <p className="job-intake-related-hint">No skills extracted yet.</p> : null}
                  </div>
                </div>

                <div
                  id="job-intake-panel-related"
                  role="tabpanel"
                  aria-labelledby="job-intake-tab-related"
                  hidden={extractionTab !== "related"}
                  className={`job-intake-tab-panel${extractionTab === "related" ? " is-active" : ""}`}
                >
                  <div className="job-intake-field job-intake-related">
                    <p className="job-intake-related-hint">
                      These are separate postings linked from this page. Add any you want to the import
                      queue — they are not part of this job&apos;s requirements.
                    </p>
                    {relatedJobs.length === 0 ? (
                      <p className="job-intake-related-hint">No related jobs found on this page.</p>
                    ) : (
                      <>
                        <ul>
                          {relatedJobs.map((job, i) => (
                            <li key={job.url || i}>
                              <div className="job-intake-related-meta">
                                <strong>{job.titleHint || job.url}</strong>
                                {job.proximitySnippet ? (
                                  <span className="job-intake-related-snippet">{job.proximitySnippet}</span>
                                ) : null}
                                <a href={job.url} target="_blank" rel="noreferrer">
                                  {job.url}
                                </a>
                              </div>
                              {!posted ? (
                                job.queuedIntakeId ? (
                                  <Link to={`/job-intake/${job.queuedIntakeId}`}>
                                    In queue #{job.queuedIntakeId}
                                  </Link>
                                ) : (
                                  <CustomButton
                                    size="small"
                                    variant="outlined"
                                    onClick={() => queueRelated(job)}
                                  >
                                    Add to queue
                                  </CustomButton>
                                )
                              ) : null}
                            </li>
                          ))}
                        </ul>
                        {!posted && relatedJobs.some((j) => !j.queuedIntakeId) ? (
                          <CustomButton variant="outlined" onClick={queueAllRelated}>
                            Add all to queue
                          </CustomButton>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </section>
      </div>

      <div className="job-intake-actions">
        {!posted ? (
          <>
            <CustomButton variant="contained" onClick={saveReview}>
              Save review
            </CustomButton>
            <CustomButton variant="contained" color="secondary" onClick={createJob}>
              Create job posting
            </CustomButton>
          </>
        ) : (
          <p>
            Posted — job #{detail?.jobID}. Corpus frozen.
          </p>
        )}
      </div>

      {employerEditId != null ? (
        <EmployerEditModal
          employerId={employerEditId}
          onClose={() => setEmployerEditId(null)}
        />
      ) : null}
    </div>
  );
}

export default function JobIntakeDetail() {
  return (
    <ModalBusyProvider>
      <JobIntakeDetailInner />
    </ModalBusyProvider>
  );
}
